import React from "react";

type PharmacyBillItem = {
  company: string;
  hsnNo: string;
  qtyUnit: string;
  productName: string;
  batch: string;
  expiry: string;
  mrp: number;
  rate: number;
  discount: number;
  taxable: number;
  cgstPct: number;
  cgstAmt: number;
  sgstPct: number;
  sgstAmt: number;
};

type PharmacyBillData = {
  storeName?: string;
  storeAddress?: string;
  dlNo?: string;
  gstin?: string;
  pan?: string;
  documentType?: string;
  invoiceNumber?: string;
  billDate?: string;
  billTime?: string;
  userName?: string;
  patientName?: string;
  patientAddress?: string;
  doctorName?: string;
  doctorAddress?: string;
  items?: PharmacyBillItem[];
  gross?: number;
  addTax?: number;
  lessDiscount?: number;
  netAmount?: number;
  amountInWords?: string;
  remarks?: string;
};

const PharmacyBill: React.FC<{ data: PharmacyBillData }> = ({ data }) => {
  const items = data.items ?? [];
  const gross = data.gross ?? 0;
  const addTax = data.addTax ?? 0;
  const lessDiscount = data.lessDiscount ?? 0;
  const netAmount = data.netAmount ?? 0;

  return (
    <div className="pharmacy-bill max-w-[800px] mx-auto text-black text-[11px] font-sans">
      {/* Header: Hospital | Patient | Invoice - 3 columns in same row */}
      <div className="grid grid-cols-3 gap-4 items-start mb-3">
        {/* Column 1: Hospital/Store Info */}
        <div>
          <div className="text-sm font-bold mb-1">
            {data.storeName ?? "MEDICAL STORE"}
          </div>
          <div className="text-[10px] mb-0.5">{data.storeAddress ?? ""}</div>
          {data.dlNo && (
            <div className="text-[9px] mt-1">D.L.No.: {data.dlNo}</div>
          )}
          {data.gstin && (
            <div className="text-[9px]">GSTIN: {data.gstin}</div>
          )}
          {data.pan && (
            <div className="text-[9px]">PAN: {data.pan}</div>
          )}
        </div>

        {/* Column 2: Patient & Doctor Info - title and value on same line */}
        <div>
          <div className="mb-1">
            <span className="font-bold">Patient: </span>
            <span>{data.patientName ?? "-"}</span>
          </div>
          <div className="mb-1">
            <span className="font-bold">Address: </span>
            <span>{data.patientAddress ?? ""}</span>
          </div>
          <div>
            <span className="font-bold">Doctor: </span>
            <span>{data.doctorName ?? "-"}</span>
          </div>
        </div>

        {/* Column 3: Invoice Info */}
        <div className="text-right">
          <div className="text-xs font-bold mb-1.5">
            {data.documentType ?? "BILL OF SUPPLY"}
          </div>
          <div>Invoice No: {data.invoiceNumber ?? "-"}</div>
          <div>Date: {data.billDate ?? "-"}</div>
          <div>Time: {data.billTime ?? "-"}</div>
          <div>User: {data.userName ?? "-"}</div>
        </div>
      </div>

      {/* Items Table */}
      <div className="overflow-x-auto my-3">
      <table className="w-full border-collapse text-[10px] min-w-[700px]">
        <thead>
          <tr>
            <th className="border border-black p-1.5 text-left w-[50px]">Com</th>
            <th className="border border-black p-1.5 text-left w-[70px]">HSN No</th>
            <th className="border border-black p-1.5 text-left w-[70px]">Qty x Unit</th>
            <th className="border border-black p-1.5 text-left">Product Name</th>
            <th className="border border-black p-1.5 text-left w-[80px]">Batch</th>
            <th className="border border-black p-1.5 text-left w-[50px]">Exp</th>
            <th className="border border-black p-1.5 text-right w-[60px]">M.R.P.</th>
            <th className="border border-black p-1.5 text-right w-[60px]">Rate</th>
            <th className="border border-black p-1.5 text-right w-[50px]">Disc</th>
            <th className="border border-black p-1.5 text-right w-[70px]">Taxable</th>
            <th className="border border-black p-1.5 text-center w-[80px]">% CGST Amt</th>
            <th className="border border-black p-1.5 text-center w-[80px]">% SGST Amt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => (
            <tr key={idx}>
              <td className="border border-black p-1.5">{row.company ?? "-"}</td>
              <td className="border border-black p-1.5">{row.hsnNo ?? "-"}</td>
              <td className="border border-black p-1.5">{row.qtyUnit ?? "-"}</td>
              <td className="border border-black p-1.5">{row.productName ?? "-"}</td>
              <td className="border border-black p-1.5">{row.batch ?? "-"}</td>
              <td className="border border-black p-1.5">{row.expiry ?? "-"}</td>
              <td className="border border-black p-1.5 text-right">
                {Number(row.mrp ?? 0).toFixed(2)}
              </td>
              <td className="border border-black p-1.5 text-right">
                {Number(row.rate ?? 0).toFixed(2)}
              </td>
              <td className="border border-black p-1.5 text-right">
                {Number(row.discount ?? 0).toFixed(2)}
              </td>
              <td className="border border-black p-1.5 text-right">
                {Number(row.taxable ?? 0).toFixed(2)}
              </td>
              <td className="border border-black p-1.5 text-center">
                {Number(row.cgstPct ?? 0).toFixed(1)} {Number(row.cgstAmt ?? 0).toFixed(2)}
              </td>
              <td className="border border-black p-1.5 text-center">
                {Number(row.sgstPct ?? 0).toFixed(1)} {Number(row.sgstAmt ?? 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Summary */}
      <div className="my-3 text-right">
        <div className="flex justify-end gap-10 mb-1">
          <span className="min-w-[80px]">GROSS:</span>
          <span>{gross.toFixed(2)}</span>
        </div>
        <div className="flex justify-end gap-10 mb-1">
          <span className="min-w-[80px]">ADD:</span>
          <span>{addTax.toFixed(2)}</span>
        </div>
        <div className="flex justify-end gap-10 mb-1">
          <span className="min-w-[80px]">LESS:</span>
          <span>{lessDiscount.toFixed(2)}</span>
        </div>
        <div className="flex justify-end gap-10 mb-1 font-bold">
          <span className="min-w-[80px]">NET AMT:</span>
          <span>{netAmount.toFixed(2)}</span>
        </div>
      </div>

      {/* Remarks */}
      {data.remarks && (
        <div className="my-3">
          <strong>Remark:</strong> {data.remarks}
        </div>
      )}

      {/* Footer messages */}
      <div className="text-[9px] my-2">Consult Your Doctor Before Using Medicine.</div>
      <div className="text-[9px] my-2">Get well soon...</div>
      <div className="text-[9px] my-2">Subject To Jurisdiction Only. E. & O.E.</div>

      {/* Amount in words */}
      <div className="italic my-2">
        Amount in Words: {data.amountInWords ?? ""}
      </div>

      {/* Signature */}
      <div className="mt-6 text-center">
        <div>For {data.storeName ?? "MEDICAL STORE"}</div>
        <div className="w-[200px] mx-auto mt-1 pt-1 border-t border-black" />
        <div>Pharmacist</div>
      </div>
    </div>
  );
};

export default PharmacyBill;
